import { Check, Sparkles } from 'lucide-react';
import { MouseEvent, ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChoosePlanButton } from '../FeatureComparison/ChoosePlanButton';
import {
  ENTERPRISE_FROM_PRICE,
  PRICING_CHAIN,
  PRICING_PLANS,
  PricingPlan,
  getPlanRelation,
} from './pricingPlans.config';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { SubscriptionIntervalEnum } from '@/Domain/enum/SubscriptionInterval.enum';
import { cn } from '@/Domain/utils/cn';
import { StripeData } from '@/UI/Subscription/Subscription.page';

// FAQ deep-link targets, tried in this order. The match phrases are localized
// per language in `pricing.faq_links`, so the links work in every locale
// (mirrors quickchat-www). Longest phrase within a target wins.
const FAQ_LINK_ORDER = [
  'ai_credits',
  'user_licenses',
  'kb_articles',
  'ai_models',
  'resolution',
  'commercial_use',
];

type FaqMatcher = { target: string; needle: string; length: number };

const buildFaqMatchers = (faqLinks: Record<string, string[]>): FaqMatcher[] =>
  FAQ_LINK_ORDER.flatMap((target) =>
    [...(faqLinks[target] ?? [])]
      .sort((a, b) => b.length - a.length)
      .map((phrase) => ({ target, needle: phrase.toLowerCase(), length: phrase.length })),
  );

// Wraps the first matching phrase (case-insensitive) in an inline link that opens
// the matching FAQ item; the matched text keeps its original casing.
const renderFeature = (
  feature: string,
  matchers: FaqMatcher[],
  onFaqLink: (faqKey: string) => void,
): ReactNode => {
  const haystack = feature.toLowerCase();
  for (const { target, needle, length } of matchers) {
    const i = haystack.indexOf(needle);
    if (i === -1) continue;
    return (
      <>
        {feature.slice(0, i)}
        <span
          role="button"
          tabIndex={0}
          data-testid={`pricing-faq-link-${target}`}
          onClick={() => onFaqLink(target)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onFaqLink(target);
            }
          }}
          className="cursor-pointer underline decoration-dotted underline-offset-2 hover:decoration-solid"
        >
          {feature.slice(i, i + length)}
        </span>
        {feature.slice(i + length)}
      </>
    );
  }
  return feature;
};

type Props = {
  plan: PricingPlan;
  isBillingYearly: boolean;
  stripeData: StripeData;
  onFaqLink: (faqKey: string) => void;
  className?: string;
};

export const PricingPlanCard = ({
  plan,
  isBillingYearly,
  stripeData,
  onFaqLink,
  className,
}: Props) => {
  const { t } = useTranslation('subscription_page');
  const ctaRef = useRef<HTMLButtonElement>(null);

  const featuresFor = (key: string): string[] =>
    t(`pricing.plans.${key}.features`, { returnObjects: true }) as unknown as string[];

  const faqMatchers = buildFaqMatchers(
    t('pricing.faq_links', { returnObjects: true }) as unknown as Record<string, string[]>,
  );

  const relation = getPlanRelation(
    plan,
    stripeData.currentPlan,
    stripeData.subscription,
    isBillingYearly,
  );

  const name = t(`tiers.${plan.key}`);
  const ctaLabel =
    relation === 'current'
      ? t('button.current_plan')
      : relation === 'upcoming'
        ? t('button.upcoming_plan')
        : relation === 'downgrade'
          ? t('pricing.cta.switch_to', { plan: t(`tiers.${plan.key}`) })
          : relation === 'update'
            ? t('pricing.cta.update')
            : plan.isCustom
              ? t('button.contact_us')
              : t('pricing.cta.upgrade');
  const disabled = relation === 'current' || relation === 'upcoming';
  const ctaVariant =
    relation === 'next' || relation === 'update'
      ? 'dark'
      : relation === 'downgrade'
        ? 'link'
        : 'secondary';
  const isOwnPlan = plan.tier === stripeData.currentPlan.tier;
  const isRecommended = relation === 'next';
  const isCardClickable =
    (relation === 'next' || relation === 'higher') && plan.key !== 'free' && !isOwnPlan;

  // Mouse-only shortcut to the CTA; clicks inside the CTA's portalled dialogs bubble here too.
  const handleCardClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target;
    const button = ctaRef.current;
    if (
      !button ||
      button.disabled ||
      !(target instanceof Element) ||
      !event.currentTarget.contains(target) ||
      target.closest('a, button, [role="button"]') ||
      window.getSelection()?.toString()
    ) {
      return;
    }
    button.click();
  };

  const chainIndex = PRICING_CHAIN.indexOf(plan.key);
  const previousKey = !plan.isCustom && chainIndex > 0 ? PRICING_CHAIN[chainIndex - 1] : null;
  const previousFeatures = previousKey ? featuresFor(previousKey) : [];
  const displayedFeatures = plan.isCustom
    ? featuresFor(plan.key)
    : featuresFor(plan.key).filter((feature) => !previousFeatures.includes(feature));
  const inheritsFrom = previousKey ? t(`tiers.${previousKey}`) : null;
  const inheritsFromOwnPlan =
    !!previousKey &&
    PRICING_PLANS.find((p) => p.key === previousKey)?.tier === stripeData.currentPlan.tier;

  const price = isBillingYearly ? plan.monthlyPrice * 10 : plan.monthlyPrice;
  const priceSuffix = isBillingYearly ? t('pricing.per_year') : t('pricing.per_month');
  const showSavings = isBillingYearly && plan.monthlyPrice > 0;
  const showTaxNote = !plan.isCustom && plan.monthlyPrice > 0 && !disabled;
  const isPayingMonthly = stripeData.currentPlan.interval === SubscriptionIntervalEnum.Monthly;
  const ownPlanMonthlyPrice = isPayingMonthly
    ? PRICING_PLANS.find((p) => p.tier === stripeData.currentPlan.tier && p.monthlyPrice > 0)
        ?.monthlyPrice
    : undefined;
  const reservesIncreaseLine = !isBillingYearly && ownPlanMonthlyPrice !== undefined;
  const monthlyIncrease =
    !isBillingYearly && ownPlanMonthlyPrice && (relation === 'next' || relation === 'higher')
      ? plan.monthlyPrice - ownPlanMonthlyPrice
      : 0;

  return (
    <div
      data-testid={`pricing-plan-${plan.key}`}
      onClick={isCardClickable ? handleCardClick : undefined}
      className={cn(
        'flex flex-col rounded-xl border bg-card p-6',
        isRecommended ? 'border-info/40 shadow-sm' : 'border-border',
        isCardClickable && 'cursor-pointer transition-colors',
        isCardClickable && (isRecommended ? 'hover:border-info/70' : 'hover:border-foreground/20'),
        className,
      )}
    >
      <div className="mb-4 flex h-6 items-center justify-between gap-2">
        <h3 className="font-semibold leading-none tracking-tight">{name}</h3>
        {isRecommended && (
          <Badge
            variant="info"
            className="gap-1 rounded-full"
            data-testid={`pricing-badge-recommended-${plan.key}`}
          >
            <Sparkles className="size-3" aria-hidden="true" />
            {t('pricing.badges.recommended')}
          </Badge>
        )}
        {isOwnPlan && (
          <Badge
            variant="secondary"
            className="rounded-full"
            data-testid={`pricing-badge-your-plan-${plan.key}`}
          >
            {t('pricing.badges.your_plan')}
          </Badge>
        )}
      </div>

      <div className="mb-5">
        {plan.isCustom ? (
          <p>
            <span className="text-sm text-muted-foreground">{t('pricing.from')} </span>
            <span
              className="text-2xl font-semibold text-foreground"
              data-testid={`pricing-price-${plan.key}`}
            >
              ${ENTERPRISE_FROM_PRICE}
            </span>
            <span className="text-sm text-muted-foreground">
              {renderFeature(t('pricing.per_resolution'), faqMatchers, onFaqLink)}
            </span>
          </p>
        ) : (
          <p>
            <span
              className="text-2xl font-semibold text-foreground"
              data-testid={`pricing-price-${plan.key}`}
            >
              ${price}
            </span>
            <span className="text-sm text-muted-foreground">{priceSuffix}</span>
            {showTaxNote && (
              <span
                className="ml-1 text-xs text-foreground-subtle"
                data-testid={`pricing-tax-note-${plan.key}`}
              >
                {t('pricing.excl_vat')}
              </span>
            )}
          </p>
        )}
        {showSavings ? (
          <p className="mt-1 text-sm text-success" data-testid={`pricing-savings-${plan.key}`}>
            {t('pricing.save')} ${plan.monthlyPrice * 2}
          </p>
        ) : monthlyIncrease > 0 ? (
          <p
            className="mt-1 min-h-10 text-sm text-muted-foreground"
            data-testid={`pricing-increase-${plan.key}`}
          >
            {t('pricing.more_than_your_plan', { amount: monthlyIncrease })}
          </p>
        ) : (
          <p className={cn('mt-1 text-sm', reservesIncreaseLine && 'min-h-10')} aria-hidden="true">
            &nbsp;
          </p>
        )}
      </div>

      <div className="mb-6" data-testid={`pricing-cta-${plan.key}`}>
        <ChoosePlanButton
          buttonRef={ctaRef}
          tier={plan.tier}
          isBillingYearly={isBillingYearly}
          stripeData={stripeData}
          label={ctaLabel}
          variant={ctaVariant}
          disabled={disabled}
          isDowngrade={relation === 'downgrade'}
          tooltip={relation === 'update' ? t('pricing.cta.update_legacy_tooltip') : undefined}
        />
      </div>

      {inheritsFrom && (
        <p className="mb-2.5 text-sm font-medium" data-testid={`pricing-inherits-${plan.key}`}>
          {inheritsFromOwnPlan
            ? t('pricing.everything_in_your_plan')
            : t('pricing.everything_in', { plan: inheritsFrom })}
        </p>
      )}
      <ul className="flex flex-col gap-2.5 text-sm text-muted-foreground">
        {displayedFeatures.map((feature) => (
          <li key={feature} className="flex items-start gap-2">
            <Check className="mt-0.5 size-4 shrink-0" />
            <span>{renderFeature(feature, faqMatchers, onFaqLink)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};
